import {
    Checkbox,
    Chip,
    FormControlLabel,
    List,
    ListItem,
    Popover,
    useTheme,
} from '@material-ui/core'
import React, { useState } from 'react'
import { Container } from '../../components'
import {
    DamageStatus,
    getDamageStatusName,
    DamageStatusCounts,
} from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    selectedStatuses: DamageStatus[]
    onStatusChange: (statuses: DamageStatus[]) => void
    statusCounts: DamageStatusCounts | null
}

export const StatusFilterChip: React.FC<Props> = ({
    selectedStatuses,
    onStatusChange,
    statusCounts,
}) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleStatusToggle = (status: DamageStatus) => {
        const newStatuses = selectedStatuses.includes(status)
            ? selectedStatuses.filter((s) => s !== status)
            : [...selectedStatuses, status]
        onStatusChange(newStatuses)
    }

    const statusLabels =
        selectedStatuses.length > 0
            ? selectedStatuses
                  .map(
                      (status) =>
                          `${getDamageStatusName(status)} (${
                              statusCounts?.[status] ?? 0
                          })`,
                  )
                  .join(', ')
            : 'All Statuses'

    const chipStyle: React.CSSProperties = {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        fontWeight: 700,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    }

    return (
        <>
            <Chip
                label={statusLabels}
                clickable
                style={chipStyle}
                onClick={handleClick}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        borderRadius: theme.shape.borderRadius,
                        boxShadow: theme.shadows[4],
                        minWidth: '240px',
                        maxHeight: '400px',
                        overflow: 'hidden',
                    },
                }}
            >
                <Container
                    style={{
                        padding: theme.spacing(2),
                        flexDirection: 'column',
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 700,
                            marginBottom: theme.spacing(2),
                            color: theme.palette.text.primary,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            paddingBottom: theme.spacing(1),
                        }}
                    >
                        Filter by Status
                    </span>
                    <List
                        style={{
                            padding: 0,
                            overflowY: 'auto',
                        }}
                    >
                        {Object.values(DamageStatus).map((status) => (
                            <ListItem
                                key={status}
                                style={{
                                    padding: theme.spacing(0.5, 0),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedStatuses.includes(
                                                status,
                                            )}
                                            onChange={() =>
                                                handleStatusToggle(status)
                                            }
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.body2,
                                                ),
                                                color:
                                                    theme.palette.text.primary,
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightMedium,
                                            }}
                                        >
                                            {getDamageStatusName(status)} (
                                            {statusCounts?.[status] ?? 0})
                                        </span>
                                    }
                                    style={{
                                        margin: 0,
                                        width: '100%',
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Container>
            </Popover>
        </>
    )
}
